import { forwardRef, useMemo } from "react";
import { FLOWERS, Fern, Butterfly } from "@/components/garden/Plants";
import { SEASON_LOOK, SeasonGround, SeasonAir, SeasonTrees } from "@/components/garden/Seasons";

export const W = 800;
export const H = 420;
const BUTTERFLY_HUES = ["#F2A65A", "#9C7BD8", "#F28FAD", "#6CC3D5", "#F6C343"];

const hash = (s) => { let h = 2166136261; for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619); return (h >>> 0) / 4294967295; };

export const skyFor = (hour) => {
  if (hour >= 5 && hour < 8) return { top: "#F9D3B4", bottom: "#FCEFE3", sun: "#F7A86B", night: false };
  if (hour >= 8 && hour < 17) return { top: "#A9D4F2", bottom: "#E6F3FB", sun: "#FFE07A", night: false };
  if (hour >= 17 && hour < 20) return { top: "#C7A6E0", bottom: "#F7C9C2", sun: "#F59E7A", night: false };
  return { top: "#24204A", bottom: "#4B4380", sun: "#F4F1DE", night: true };
};

// Places items deterministically so the same garden always looks the same
const layout = (items, kind) => items.map((it) => {
  const a = hash(it.id), b = hash(`${it.id}-y`);
  const y = 270 + b * 120;
  return { ...it, type: kind, x: 30 + a * (W - 60), y, scale: 1.0 + (y - 270) / 170 };
});

const Sky = ({ sky, look, season }) => (
  <>
    <defs>
      <linearGradient id="mm-sky" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={sky.top} /><stop offset="1" stopColor={sky.bottom} /></linearGradient>
      <linearGradient id="mm-hill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={look.front[0]} /><stop offset="1" stopColor={look.front[1]} /></linearGradient>
    </defs>
    <rect width={W} height={H} fill="url(#mm-sky)" />
    {sky.night && Array.from({ length: 28 }, (_, i) => <circle key={i} cx={hash(`s${i}`) * W} cy={hash(`t${i}`) * 170} r={hash(`r${i}`) * 1.4 + 0.4} fill="#fff" opacity={0.5 + hash(`o${i}`) * 0.5} />)}
    <circle cx={W - 110} cy={80} r={sky.night ? 26 : 34} fill={sky.sun} opacity={sky.night ? 0.95 : 0.9} />
    {!sky.night && [[140, 70], [420, 50]].map(([x, y]) => (
      <g key={x} fill="#fff" opacity="0.8"><ellipse cx={x} cy={y} rx="44" ry="14" /><ellipse cx={x + 22} cy={y - 10} rx="26" ry="14" /></g>
    ))}
    <path d={`M0 250 Q200 205 420 245 T${W} 235 V${H} H0z`} fill={look.back} />
    <SeasonTrees season={season} />
    <path d={`M0 290 Q260 250 520 285 T${W} 280 V${H} H0z`} fill="url(#mm-hill)" />
    {Array.from({ length: 46 }, (_, i) => {
      const x = hash(`g${i}`) * W, y = 285 + hash(`h${i}`) * 130, s = 0.7 + (y - 285) / 120;
      return <path key={i} d="M0 0 l-4 -9 M0 0 l0 -12 M0 0 l4 -9" stroke={look.grass} strokeWidth="1.6" strokeLinecap="round" opacity="0.7" transform={`translate(${x.toFixed(1)},${y.toFixed(1)}) scale(${s.toFixed(2)})`} />;
    })}
    {Array.from({ length: 22 }, (_, i) => (
      <circle key={`d${i}`} cx={hash(`p${i}`) * W} cy={292 + hash(`q${i}`) * 120} r={1.8 + hash(`z${i}`) * 1.4} fill={look.dots[i % 3]} opacity="0.9" />
    ))}
    <SeasonGround season={season} w={W} />
  </>
);

const Item = ({ it, fresh, onPick, interactive }) => {
  const Flower = FLOWERS[it.kind];
  const body = it.type === "flower" ? <Flower color={it.color} /> : <Fern flip={hash(it.id) > 0.5} />;
  const click = interactive && it.type === "flower" ? { onClick: () => onPick(it), role: "button", tabIndex: 0, "aria-label": "Flower details", onKeyDown: (e) => e.key === "Enter" && onPick(it), style: { cursor: "pointer" } } : {};
  return (
    <g className={interactive && it.type === "flower" ? "mm-flower" : undefined} transform={`translate(${it.x.toFixed(1)},${it.y.toFixed(1)}) scale(${it.scale.toFixed(2)})`} data-testid={interactive ? `garden-${it.type}-${it.id}` : undefined} {...click}>
      <g className={`mm-sway ${fresh ? "mm-grow" : ""}`} style={{ animationDelay: `${(hash(it.id) * 3).toFixed(2)}s` }}>
        {interactive && it.type === "flower" && <rect x="-18" y="-95" width="40" height="100" fill="transparent" />}
        {body}
      </g>
    </g>
  );
};

// A small floating heart for each supporter hug this month
const HugHearts = ({ hugs }) => hugs.length > 0 && (
  <g data-testid="garden-hug-hearts" pointerEvents="none">
    {hugs.slice(0, 12).map((h) => (
      <g key={h.id} transform={`translate(${(hash(`${h.id}h`) < 0.5 ? 50 + hash(`${h.id}x`) * 210 : W - 260 + hash(`${h.id}x`) * 210).toFixed(1)},${(110 + hash(`${h.id}v`) * 120).toFixed(1)})`}>
        <g className="mm-float" style={{ animationDelay: `${(hash(h.id) * 5).toFixed(2)}s`, animationDuration: "9s" }}>
          <path d="M0 6 C-12 -4 -8 -14 0 -8 C8 -14 12 -4 0 6z" fill="#F28FAD" stroke="#D9577F" strokeWidth="1.2" opacity="0.95" />
        </g>
      </g>
    ))}
  </g>
);

// One sparkle per Skill of the Day streak day, with a soft glow that grows with the streak
const StreakSparkles = ({ n }) => n > 0 && (
  <g data-testid="garden-streak-sparkles" pointerEvents="none">
    <ellipse className="mm-glow" cx={W / 2} cy={330} rx={160 + n * 14} ry={50 + n * 3} fill="#FFE7A0" opacity="0.35" style={{ filter: "blur(18px)" }} />
    {Array.from({ length: n }, (_, i) => {
      const x = 40 + hash(`sp${i}`) * (W - 80), y = 150 + hash(`sq${i}`) * 200, r = 9 + hash(`sr${i}`) * 6;
      return (
        <g key={i} transform={`translate(${x.toFixed(1)},${y.toFixed(1)})`}>
          <path className="mm-sparkle" style={{ animationDelay: `${(hash(`sd${i}`) * 2.4).toFixed(2)}s`, filter: "drop-shadow(0 0 4px rgba(255,200,40,.9))" }} d={`M0 ${-r} Q0 0 ${r} 0 Q0 0 0 ${r} Q0 0 ${-r} 0 Q0 0 0 ${-r}z`} fill="#FFE066" stroke="#E0A100" strokeWidth="1.4" />
        </g>
      );
    })}
  </g>
);

export const GardenScene = forwardRef(({ data, seenAt, onPick, interactive = false, title, className = "" }, ref) => {
  const season = SEASON_LOOK[data.season] ? data.season : "spring";
  const base = skyFor(title ? 11 : data.local_hour);
  const sky = season === "winter" && !base.night ? { ...base, top: "#B9C7D8", bottom: "#E6EDF4", sun: "#F7F3E6" } : base;
  const items = useMemo(() => [...layout(data.flowers, "flower"), ...layout((data.golden || []).map((g) => ({ ...g, kind: "marigold", golden: true })), "flower"), ...layout((data.friends || []).map((g) => ({ ...g, friend: true })), "flower"), ...layout(data.greens, "green")].sort((a, b) => a.y - b.y), [data]);
  const flies = useMemo(() => data.butterflies.slice(0, 12).map((b, i) => ({ ...b, x: 60 + hash(b.id) * (W - 120), y: 120 + hash(`${b.id}b`) * 110, hue: BUTTERFLY_HUES[i % 5] })), [data]);
  return (
    <svg ref={ref} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" className={`w-full h-auto block ${className}`} role="img"
      aria-label={`${data.month_name} garden with ${data.flowers.length} flowers, ${data.greens.length} ferns and ${data.butterflies.length} butterflies`}>
      <Sky sky={sky} look={SEASON_LOOK[season]} season={season} />
      {items.map((it) => <Item key={`${it.type}-${it.id}`} it={it} fresh={!!seenAt && it.at > seenAt} onPick={onPick} interactive={interactive} />)}
      {flies.map((b) => (
        <g key={b.id} transform={`translate(${b.x.toFixed(1)},${b.y.toFixed(1)})`}><g className="mm-float" style={{ animationDelay: `${(hash(b.id) * 4).toFixed(2)}s` }}><Butterfly hue={b.hue} /></g></g>
      ))}
      <SeasonAir season={season} night={sky.night} w={W} h={H} />
      <HugHearts hugs={data.hugs || []} />
      <StreakSparkles n={Math.min(data.skill_streak || 0, 21)} />
      {title && (
        <g>
          <rect x="24" y="22" width={title.length * 15 + 40} height="52" rx="26" fill="#fff" opacity="0.85" />
          <text x="44" y="57" fontFamily="Georgia, 'Times New Roman', serif" fontSize="26" fill="#5B3F8F">{title}</text>
        </g>
      )}
    </svg>
  );
});
GardenScene.displayName = "GardenScene";
